// src/components/credits/CreditUsageTable.jsx
//
// Renders a CreditUsageResponse's per-user buckets.
//   - Members get email: null -> the Utente column falls back to a short user id.
//   - buckets: [] -> empty-state row (never a spinner/error).
//
// TWO SCALES, NEVER MIXED — the customer-facing figure is the PRODUCT scale:
//   - product_credits / total_product_credits  -> what a customer is sold and
//     what the plan allowance is denominated in. This is the primary column and
//     the only one a non-superadmin ever sees.
//   - credits / total_credits                  -> the internal COST scale. It
//     includes llm_eval + embedding spend and runs ~150x larger, so showing it
//     to a customer beside a balance card reading "8" is simply wrong. Rendered
//     only behind showMoney (superadmin), beside cost_usd.
//
// INVARIANT (the reason a percentage is legitimate on one field only):
//   total_product_credits EQUALS /credits/balance's consumed_credits over the
//   same window, so a "% of allowance" reading off total_product_credits is
//   sound. total_credits must NEVER be reconciled against the balance — it
//   counts spend the allowance does not. Related: a non-zero bucket with zero
//   runs is legitimate on /usage (cost accrues outside run rows), so we never
//   render a reconciliation warning here.
//
// GRACEFUL DEGRADATION: an old backend omits product_credits entirely. Fall
// back to the cost-scale field so the column never blanks — but do NOT relabel
// it: the header stays the neutral "Crediti" either way.
//
// TWO AXES (groupBy):
//   - 'user'   (default) per-user buckets of THIS tenant's spend; key user_id.
//   - 'tenant' (PB5)     the CUSTOMER's wallet split per workspace; key tenant_id.
//     Buckets carry only tenant_id, so the caller passes resolveTenantName
//     (tenant_id → display name); unresolved ids fall back to a short id, and the
//     row matching `activeTenantId` is flagged "(questo workspace)". The
//     product scale is rendered on both axes, consistent with Phase 1.
import React from 'react';
import { useTranslation } from 'react-i18next';
import AdminTable from '../../admin/components/AdminTable.jsx';
import { formatCredits, formatMoney } from '../../utils/creditsFormat.js';
import { COLORS, FONT_SANS } from '../../constants/theme.js';

const FONT = FONT_SANS;
const COLOR = { text: COLORS.ink, muted: COLORS.textSecondary, border: COLORS.border };
const cardStyle = {
  background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLOR.border}`,
  padding: '20px 22px', fontFamily: FONT, color: COLOR.text,
};

// Presence test, not truthiness: '0' / 0 are legitimate product-credit values,
// while a missing field means "backend predates product credits".
const has = (v) => v !== undefined && v !== null;

// Exported so any other reader of a CreditUsageResponse (e.g. the per-workspace
// wallet split) resolves the customer-facing scale the same way.
/** Customer-facing credits for a bucket, falling back to the cost scale on an old backend. */
export function productCreditsOf(row) {
  return has(row?.product_credits) ? row.product_credits : row?.credits;
}

/** Customer-facing total for the response, same fallback. */
export function totalProductCreditsOf(usage) {
  return has(usage?.total_product_credits) ? usage.total_product_credits : usage?.total_credits;
}

export default function CreditUsageTable({
  usage, showMoney = false, loading = false, groupBy = 'user', resolveTenantName, activeTenantId,
}) {
  const { t } = useTranslation('credits');
  const buckets = usage?.buckets || [];
  const byTenant = groupBy === 'tenant';

  const firstColumn = byTenant
    ? {
        key: 'tenant', label: t('usage.columns.workspace'),
        render: (row) => {
          const name = (resolveTenantName && row.tenant_id && resolveTenantName(row.tenant_id))
            || (row.tenant_id ? `${row.tenant_id.slice(0, 8)}…` : t('usage.unknownWorkspace'));
          const isCurrent = activeTenantId && row.tenant_id === activeTenantId;
          return isCurrent ? <>{name} <span style={{ color: COLOR.muted, fontSize: 11 }}>{t('usage.thisWorkspace')}</span></> : name;
        },
      }
    : {
        key: 'user', label: t('usage.columns.user'),
        render: (row) => row.email || (row.user_id ? `${row.user_id.slice(0, 8)}…` : t('usage.unknownUser')),
      };

  const columns = [
    firstColumn,
    {
      key: 'credits', label: t('usage.columns.credits'), width: '140px',
      render: (row) => formatCredits(productCreditsOf(row)),
    },
    // Cost scale + $ — superadmin only. Never rendered beside the product scale
    // for a customer, so the two can't be read as the same number.
    ...(showMoney ? [
      {
        key: 'cost_credits', label: t('usage.columns.costCredits'), width: '140px',
        render: (row) => formatCredits(row.credits),
      },
      {
        key: 'cost_usd', label: t('usage.columns.cost'), width: '120px',
        render: (row) => formatMoney(row.cost_usd) || '—',
      },
    ] : []),
  ];

  // On an old backend both scales resolve to the same field, so the superadmin
  // sees the one number twice rather than a wrong one — the honest degradation.
  const totalCostCredits = showMoney && has(usage?.total_credits) ? formatCredits(usage.total_credits) : null;
  const totalCostUsd = showMoney ? formatMoney(usage?.total_cost_usd) : null;

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>{byTenant ? t('usage.titleByTenant') : t('usage.title')}</h3>
        {usage && (
          <span style={{ fontSize: 12, color: COLOR.muted }}>
            {t('usage.total')} <strong style={{ color: COLOR.text }}>{formatCredits(totalProductCreditsOf(usage))}</strong> {t('usage.totalSuffix')}
            {totalCostCredits ? ` · ${t('usage.totalCostScale', { credits: totalCostCredits })}` : ''}
            {totalCostUsd ? ` · ${totalCostUsd}` : ''}
          </span>
        )}
      </div>
      <AdminTable
        columns={columns}
        rows={buckets}
        loading={loading}
        emptyMessage={t('usage.empty')}
        getRowKey={(row, i) => (byTenant ? row.tenant_id : row.user_id) || `none-${i}`}
      />
    </div>
  );
}
